import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, History, Send, X } from 'lucide-react';
import { getFaqApiAiFaqGet, getHomeApiAiHomeGet, type AiFaqItem, type AiHome, type AiRecentChat, type AiTopic } from '@/lib/api/generated';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth/auth-context';
import { useMyPermissions } from '@/lib/auth/my-permissions';
import { useLocalePrefix } from '@/lib/hooks/useLocaleNavigate';
import { bindChatToUser, openRecentChat, sendQuestion, setUnread, showHome, useChat } from './chatStore';

/**
 * GEN-12-02 / -03 — the Yakson AI panel. Opens from the right over the screen in
 * use (which stays behind as it was); × or a click on the dark background closes
 * it. Before she types it shows the home: Ask Sunny · Recent chats · Topics.
 * Phase 1 answers by searching the Knowledge documents. Only HQ roles see the
 * source of an answer; staff never get a document, file name or link (GEN-12-04).
 * Yakson AI only answers: it never sends, pays, approves, books or changes anything.
 */
export function YaksonAiPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation('yaksonAi');
  const prefix = useLocalePrefix();
  const { user } = useAuth();
  const { isHq } = useMyPermissions();
  const chat = useChat();
  const [draft, setDraft] = useState('');
  // GEN-12-09 — the FAQ: HQ's Q&A rows in FAQ order that her role may ask.
  const [faqOpen, setFaqOpen] = useState(false);
  const [faq, setFaq] = useState<AiFaqItem[] | null>(null);
  const [faqSearch, setFaqSearch] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bindChatToUser(user?.id ?? null);
  }, [user?.id]);

  // The home (Recent chats, Topics) is read each time the panel opens or goes back home.
  const [home, setHome] = useState<AiHome | null>(null);
  const [topic, setTopic] = useState<AiTopic | null>(null);
  useEffect(() => {
    if (!user?.id || !chat.home) return;
    let live = true;
    getHomeApiAiHomeGet()
      .then((r) => {
        if (!live) return;
        setHome(r);
        setUnread(r.unreadCount ?? 0);
      })
      .catch(() => live && setHome({ unreadCount: 0, recent: [], topics: [] }));
    return () => {
      live = false;
    };
  }, [user?.id, chat.home]);

  const openRecent = (r: AiRecentChat) =>
    void openRecentChat(
      r,
      (question, answer) =>
        t('panel.hqAnswered', {
          defaultValue: 'I checked with HQ about your question “{question}”: {answer}',
          question,
          answer,
          interpolation: { escapeValue: false },
        }),
      (date) => t('panel.hqAnsweredSource', { defaultValue: 'Answered by HQ · {date}', date }),
    );

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [chat.messages.length, chat.typing]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ask = () => {
    const q = draft;
    setDraft('');
    void sendQuestion(q, i18n.language, t('panel.failed'));
  };

  const askTopicQuestion = (q: string) => void sendQuestion(q, i18n.language, t('panel.failed'));
  // A topic with no suggested question pre-fills the box with the start of a question.
  const startTopic = (a: AiTopic) => {
    if (a.questions && a.questions.length > 0) {
      setTopic(topic?.area === a.area ? null : a);
      return;
    }
    setTopic(null);
    setDraft(t('panel.topicStart', { defaultValue: 'About {area}: ', area: a.area, interpolation: { escapeValue: false } }));
    homeInputRef.current?.focus();
  };
  const homeInputRef = useRef<HTMLInputElement>(null);

  const toggleFaq = () => {
    const next = !faqOpen;
    setFaqOpen(next);
    if (next) {
      // Read each time it opens, so a new FAQ order shows (GEN-12-17).
      getFaqApiAiFaqGet()
        .then((r) => setFaq(r.items ?? []))
        .catch(() => setFaq([]));
    }
  };

  const askFaq = (item: AiFaqItem) => {
    setFaqOpen(false);
    void sendQuestion(item.question, i18n.language, t('panel.failed'));
  };

  const faqGroups = (() => {
    const q = faqSearch.trim().toLowerCase();
    const groups: { area: string; items: AiFaqItem[] }[] = [];
    for (const item of faq ?? []) {
      if (q && !item.question.toLowerCase().includes(q)) continue;
      const area = item.area || t('panel.faqOther', { defaultValue: 'Other' });
      const group = groups.find((g) => g.area === area);
      if (group) group.items.push(item);
      else groups.push({ area, items: [item] });
    }
    return groups;
  })();

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="yakson-ai-backdrop">
      <aside
        role="dialog"
        aria-label={t('panel.title')}
        className="flex h-full w-full max-w-[440px] flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="yakson-ai-panel"
      >
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          {!chat.home && (
            <button
              type="button"
              aria-label={t('panel.home', { defaultValue: 'Home' })}
              onClick={showHome}
              className="rounded p-1 hover:bg-[#F7F7F7]"
              data-testid="yakson-ai-home-back"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <h2 className="flex-1 text-base font-semibold">{t('panel.title')}</h2>
          {isHq && (
            <Link
              to={`${prefix}/hr/yakson-ai/history`}
              onClick={onClose}
              className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs hover:bg-[#FBF6F7]"
              data-testid="yakson-ai-history"
            >
              <History className="h-3.5 w-3.5" /> {t('panel.history')}
              <span className="rounded bg-[#FF8189] px-1 text-[10px] font-semibold text-white">HQ</span>
            </Link>
          )}
          <button type="button" aria-label={t('close')} onClick={onClose} className="rounded p-1 hover:bg-[#F7F7F7]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
        {faqOpen && (
          <div className="absolute inset-0 z-10 flex flex-col bg-white" data-testid="yakson-ai-faq">
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <h3 className="text-sm font-semibold">{t('panel.faqTitle', { defaultValue: 'Frequently asked questions' })}</h3>
              <button type="button" onClick={() => setFaqOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">
                {t('close')}
              </button>
            </div>
            <div className="px-4 py-2">
              <input
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                placeholder={t('panel.faqSearch', { defaultValue: 'Search FAQ' })}
                className="w-full rounded-lg border border-border px-3 py-1.5 text-sm focus:border-[#FF8189] focus:outline-none"
              />
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
              {faq === null ? (
                <p className="text-xs text-muted-foreground">…</p>
              ) : faqGroups.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('panel.faqNone', { defaultValue: 'No FAQ yet.' })}</p>
              ) : (
                faqGroups.map((g) => (
                  <div key={g.area}>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.area}</p>
                    <ul className="space-y-1">
                      {g.items.map((item) => (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => askFaq(item)}
                            className="w-full rounded-lg border border-border px-3 py-2 text-left text-sm hover:border-[#FF8189] hover:bg-[#FFF5F6]"
                          >
                            {item.question}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {chat.home ? (
          <HomeView
            home={home}
            topic={topic}
            draft={draft}
            setDraft={setDraft}
            inputRef={homeInputRef}
            onAsk={ask}
            onTopic={startTopic}
            onTopicQuestion={askTopicQuestion}
            onRecent={openRecent}
            typing={chat.typing}
          />
        ) : (
        <div className="flex-1 space-y-3 overflow-y-auto bg-[#FBF6F7] px-4 py-4" data-testid="yakson-ai-chat">
          <Bubble from="sunny">
            <p>{t('panel.hello1')}</p>
            <p>{t('panel.hello2')}</p>
          </Bubble>
          {chat.messages.map((m) => (
            <Bubble key={m.id} from={m.from}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              {m.sources && m.sources.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-border pt-2 text-xs text-muted-foreground">
                  {m.sources.map((s, i) => (
                    <li key={i}>
                      {t('panel.source')}: {s.title}{s.version ? ` · ${s.version}` : ''}
                      {s.ref ? ` · ${s.ref}` : ''}
                      {isHq && s.sourceFile ? ` (${s.sourceFile})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </Bubble>
          ))}
          {chat.typing && (
            <p className="text-xs text-muted-foreground" role="status">
              {t('panel.typing')}
            </p>
          )}
          <div ref={endRef} />
        </div>
        )}
        </div>

        {!chat.home && (
        <form
          className="flex items-center gap-2 border-t border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={1000}
            placeholder={t('panel.placeholder')}
            className="min-w-0 flex-1 rounded-lg border border-border px-3 py-2 text-sm focus:border-[#FF8189] focus:outline-none focus:ring-1 focus:ring-[#FF8189]"
            data-testid="yakson-ai-input"
            autoFocus
          />
          <button
            type="button"
            onClick={toggleFaq}
            aria-pressed={faqOpen}
            className={cn(
              'rounded-lg border px-3 py-2 text-sm font-medium',
              faqOpen ? 'border-[#FF8189] bg-[#FF8189] text-white' : 'border-border text-foreground hover:bg-[#FBF6F7]',
            )}
            data-testid="yakson-ai-faq-button"
          >
            FAQ
          </button>
          <button
            type="submit"
            disabled={!draft.trim() || chat.typing}
            className="rounded-lg bg-[#FF8189] px-4 py-2 text-sm font-medium text-white hover:bg-[#f76b74] disabled:opacity-50"
            data-testid="yakson-ai-ask"
          >
            {t('panel.ask')}
          </button>
        </form>
        )}
      </aside>
    </div>
  );
}

interface HomeViewProps {
  home: AiHome | null;
  topic: AiTopic | null;
  draft: string;
  setDraft: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onAsk: () => void;
  onTopic: (a: AiTopic) => void;
  onTopicQuestion: (q: string) => void;
  onRecent: (r: AiRecentChat) => void;
  typing: boolean;
}

/** GEN-12-02 — the panel home: Ask Sunny · Recent chats · Topics. */
function HomeView({ home, topic, draft, setDraft, inputRef, onAsk, onTopic, onTopicQuestion, onRecent, typing }: HomeViewProps) {
  const { t } = useTranslation('yaksonAi');
  const unread = home?.unreadCount ?? 0;
  return (
    <div className="flex-1 space-y-5 overflow-y-auto bg-[#FBF6F7] px-4 py-4" data-testid="yakson-ai-home">
      <div>
        <p className="text-base font-semibold">{t('panel.hello1')}</p>
        <p className="text-sm text-muted-foreground">{t('panel.hello2')}</p>
        <form
          className="mt-3 flex items-center gap-2 rounded-2xl border border-border bg-white p-2 shadow-sm focus-within:border-[#FF8189]"
          onSubmit={(e) => {
            e.preventDefault();
            onAsk();
          }}
        >
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={1000}
            placeholder={t('panel.askSunny', { defaultValue: 'Ask Sunny' })}
            aria-label={t('panel.askSunny', { defaultValue: 'Ask Sunny' })}
            className="min-w-0 flex-1 bg-transparent px-2 py-1.5 text-sm focus:outline-none"
            data-testid="yakson-ai-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={!draft.trim() || typing}
            aria-label={t('panel.ask')}
            className="rounded-xl bg-[#FF8189] p-2 text-white hover:bg-[#f76b74] disabled:opacity-50"
            data-testid="yakson-ai-ask"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>

      <section data-testid="yakson-ai-recent">
        <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t('panel.recentChats', { defaultValue: 'Recent chats' })}
          {unread > 0 && (
            <span className="rounded-full bg-red-500 px-1.5 text-[10px] font-semibold leading-4 text-white" data-testid="yakson-ai-recent-unread">
              {unread}
            </span>
          )}
        </h3>
        {home === null ? (
          <p className="text-xs text-muted-foreground">…</p>
        ) : (home.recent ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('panel.recentNone', { defaultValue: 'No chats yet. Ask Sunny anything.' })}</p>
        ) : (
          <ul className="space-y-1.5">
            {(home.recent ?? []).map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onRecent(r)}
                  className="flex w-full items-center gap-2 rounded-xl border border-border bg-white px-3 py-2 text-left text-sm hover:border-[#FF8189]"
                  data-testid="yakson-ai-recent-item"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{r.question}</span>
                    <span className="block text-[11px] text-muted-foreground">{new Date(r.askedAt).toLocaleDateString('en-GB')}</span>
                  </span>
                  {r.unread && (
                    <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {t('panel.newAnswer', { defaultValue: 'New answer' })}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(home?.topics ?? []).length > 0 && (
        <section data-testid="yakson-ai-topics">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('panel.topics', { defaultValue: 'Topics' })}
          </h3>
          <div className="flex flex-wrap gap-2">
            {(home?.topics ?? []).map((a) => (
              <button
                key={a.area}
                type="button"
                onClick={() => onTopic(a)}
                aria-pressed={topic?.area === a.area}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  topic?.area === a.area ? 'border-[#FF8189] bg-[#FF8189] text-white' : 'border-border bg-white hover:border-[#FF8189]',
                )}
                data-testid="yakson-ai-topic"
              >
                {a.area}
              </button>
            ))}
          </div>
          {topic && (
            <ul className="mt-2 space-y-1.5" data-testid="yakson-ai-topic-questions">
              {(topic.questions ?? []).map((q) => (
                <li key={q.id}>
                  <button
                    type="button"
                    onClick={() => onTopicQuestion(q.question)}
                    className="w-full rounded-xl border border-border bg-white px-3 py-2 text-left text-sm hover:border-[#FF8189] hover:bg-[#FFF5F6]"
                  >
                    {q.question}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function Bubble({ from, children }: { from: 'me' | 'sunny'; children: React.ReactNode }) {
  return (
    <div className={from === 'me' ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={
          from === 'me'
            ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-[#FF8189] px-3 py-2 text-sm text-white'
            : 'max-w-[85%] rounded-2xl rounded-bl-sm border border-border bg-white px-3 py-2 text-sm'
        }
      >
        {children}
      </div>
    </div>
  );
}
